import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { debounce } from 'lodash';
import printJS from 'print-js';
import { useSelector } from 'react-redux';
import { Document, Page } from 'react-pdf';
import { saveAs } from '@progress/kendo-file-saver';
import PdfDocumentControls from './pdfDocumentControls/pdfDocumentControls';
import PdfTitle from './pdfTitle/pdfTitle';
import { setPin, removePin, registerDownload } from '../../../common/services/http/documentsService';
import { RootState } from '../../../redux/store';
import ComponentLoader from '../loaders/componentLoader/componentLoader';

import './pdfViewer.scss';
import useTranslate from '../../../common/hooks/useTranslate';

interface IBasePdfViewer {
  id?: string;
  loading?: boolean;
  file?: any;
  returnPath?: string;
  document?: {
    label?: string;
    id?: number;
  };
  title?: boolean;
  shouldPin?: boolean;
  controlsBcgColor?: string;
  className?: string;
}

const getZoomFromLS = () => {
  return window.localStorage.getItem('zoom') ? parseFloat(window.localStorage.getItem('zoom')) : 1;
};

const PdfViewer: React.FC<IBasePdfViewer> = ({
                                               title,
                                               file,
                                               document,
                                               shouldPin,
                                               loading,
                                               controlsBcgColor,
                                               className,
                                             }) => {
  const [numPages, setNumPages] = useState(null);
  const [pdfDocument, setPdfDocument] = useState(null);
  const [focusedPage, setFocusedPage] = useState(1);
  const [screenSize, setScreenSize] = useState(0);
  const [pages, setPages] = useState([]);
  const [zoom, setZoom] = useState(getZoomFromLS());
  const [pinned, setPinned] = useState(false);
  const [expand, setExpand] = useState(false);
  const navigate = useNavigate();
  const impersonate = useSelector((state: RootState) => state.impersonate.impersonate);
  const { t } = useTranslate();

  const [size, setSize] = useState({
    x: window.innerWidth,
    y: window.innerHeight,
  });

  const data = useMemo(() => {
    if (file) {
      return { data: file.uint8 };
    } else {
      return null;
    }
  }, [file]);

  const containerRef = useRef(null);

  const margin = 40;

  function documentLoaded(pdfDoc: any) {
    setNumPages(pdfDoc.numPages);
    setPdfDocument(pdfDoc);
  }

  function loadPages(pagesToLoad: any, zoomFactor: any) {
    let startY = 0;
    let endY = 0;

    let loaded = pagesToLoad.map((page: any, index: any) => {
      let width = size.x * 0.8 * zoomFactor;
      let factor = width / page.view[2];
      let height = Math.trunc(page.view[3] * factor);

      startY = endY;
      endY += height + margin;

      return {
        pageIndex: index,
        page: page,
        show: index >= focusedPage - 2 && index <= focusedPage,
        width: width,
        height: height,
        startY: startY,
        endY: endY++,
      };
    });
    setPages(loaded);
    return loaded;
  }

  function focusPage(pageIndex: any) {
    setFocusedPage(pageIndex + 1);
    setPages(
      pages.map((page, index) => {
        page.show = index >= pageIndex - 1 && index <= pageIndex + 1;
        return page;
      }),
    );
  }

  const handleScroll = debounce((event) => {
    let container = event.target;
    let pageIndex = -1;

    pages.find((page, index) => {
      if (page.startY <= container.scrollTop && page.endY >= container.scrollTop) {
        pageIndex = index;
        return true;
      }
      return false;
    });

    if (pageIndex > -1) {
      focusPage(pageIndex);
    }
  }, 50);

  function onScroll(event: any) {
    handleScroll(event);
  }

  function zoomIn() {
    changeZoom(zoom + 0.2);
  }

  function zoomOut() {
    changeZoom(zoom - 0.2);
  }

  function changeZoom(zoomFactor: any) {
    if (zoomFactor <= 0.1) return;
    setZoom(zoomFactor);
    window.localStorage.setItem('zoom', zoomFactor);
    let pagesLoaded = loadPages(
      pages.map((page) => page.page),
      zoomFactor,
    );
    scrollToPage(pagesLoaded[focusedPage - 1], 'auto');
  }

  function nextPage() {
    let pageNum = focusedPage + 1;
    if (pageNum > numPages) return;
    setFocusedPage(pageNum);
    // @ts-ignore
    scrollToPage(pages[pageNum - 1]);
  }

  function previousPage() {
    let pageNum = focusedPage - 1;
    if (pageNum < 1) return;
    setFocusedPage(pageNum);
    // @ts-ignore
    scrollToPage(pages[pageNum - 1]);
  }

  function scrollToPage(page: any, scrollBehavior: any) {
    containerRef.current.scrollTo({ top: page.startY, behavior: scrollBehavior || 'smooth' });
  }

  function handlePrint() {
    printJS(URL.createObjectURL(file.blob));
  }

  function handleDownload() {
    // @ts-ignore
    saveAs(file.blob, document ? document.name : 'agreements');

    if (document) {
      registerDownload(document);
    }
  }

  const clickBackHandler = () => {
    navigate(-1);
  };

  const pinHandler = () => {
    if (!impersonate) {
      if (document) {
        if (pinned === false) {
          setPin(document.id).then(() => {
            setPinned(true);
          });
        } else if (pinned === true) {
          removePin(document.id).then(() => {
            setPinned(false);
          });
        }
      }
    }
  };

  const updateSize = () => {
    setSize({
      x: window.innerWidth,
      y: window.innerHeight,
    });
  };

  const resize = debounce(updateSize, 100);

  const onExpand = () => {
    setExpand(true);
  };
  const onCollapse = () => {
    setExpand(false);
  };

  useEffect(() => {
    if (!pdfDocument) return;

    let promises = [];
    for (let i = 0; i < numPages; i++) {
      promises.push(pdfDocument.getPage(i + 1));
    }

    Promise.all(promises).then((resolved) => {
      loadPages(resolved, zoom);
    });
  }, [pdfDocument]);

  useEffect(() => {
    pages.length > 0 && changeZoom(zoom);
  }, [size]);

  useEffect(() => {
    setScreenSize(screen.width);
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
    };
  }, []);

  useEffect(() => {
    if (shouldPin) {
      // @ts-ignore
      setPinned(document.isPinned);
    }
  }, [document]);

  return (
    <div className={`pdfViewer-container ${expand ? 'expanded-pdf-container' : ''} ${className}`}>
      {title && <PdfTitle title={document?.label} />}
      <PdfDocumentControls
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        zoom={zoom}
        numPages={numPages}
        onNextPage={nextPage}
        onPreviousPage={previousPage}
        onPrint={handlePrint}
        page={focusedPage}
        onDownload={handleDownload}
        onPin={pinHandler}
        pinned={pinned}
        shouldPin={shouldPin}
        onBackClick={clickBackHandler}
        expandClick={onExpand}
        collapseClick={onCollapse}
        expand={expand}
        backgroundColor={controlsBcgColor}
      />
      {data.data ? (
        <div className={`pdfDocument-container `} onScroll={onScroll} ref={containerRef}>
          {loading ? (
            <div className='loader-wrapper'>
              <ComponentLoader />
            </div>
          ) : (
            <Document className='pdfDocument' file={data} onLoadSuccess={documentLoaded}>
              {pages.map((page, index) => {
                  return (
                    <div key={page.pageIndex} className='pdfPage'
                         style={{ height: page.height, width: page.width, margin: `0 auto` }}>
                      {page.show && <Page pageNumber={index + 1} width={page.width} height={page.height} />}
                    </div>
                  );
              })}
            </Document>
          )}
        </div>
      ) : (
        <div className='pdfNoDocument-container'>
          <h1>{t('PDF_VIEWER.NO_DOCUMENT')}</h1>
        </div>
      )}
    </div>
  );
};

export default PdfViewer;
